import { useQuery } from '@tanstack/react-query';
import { visitApi } from 'src/api/visit';
import OrganizationVisitModal from 'src/sections/@dashboard/organization/components/OrganizationVisitModal/OrganizationVisitModal';
import { useOrganizationContext } from 'src/sections/@dashboard/organization/hooks/useOrganizationContext';
import VisitModal from 'src/sections/@dashboard/visit/components/VisitModal';

interface MedicationSourceVisitModalProps {
  /** The visit to show, or `''` when nothing is selected. */
  visitId: string;
  profileId?: string;
  onClose: () => void;
}

/**
 * The prescribing visit behind a medication row, opened the way the patient's
 * visit history opens one: staff get the organization modal, an owner gets
 * theirs.
 *
 * There is no endpoint for a single visit, so this reads the patient's history
 * — the same list the history tab renders — and picks the visit out of it. The
 * query only runs once a row has actually been clicked.
 */
export default function MedicationSourceVisitModal({
  visitId,
  profileId,
  onClose,
}: MedicationSourceVisitModalProps) {
  const { isProfessionalRoleSelected, isOrganizationAdminRoleSelected } =
    useOrganizationContext();

  const { data } = useQuery(
    ['medication-source-visit-history', profileId],
    () =>
      visitApi.visitControllerGetProfileVisitHistoryPreview({
        profileId: profileId || '',
        limit: 200,
      }),
    { enabled: Boolean(visitId && profileId), staleTime: 60 * 1000 },
  );

  const visit = data?.data.data.find((candidate) => candidate.id === visitId);
  const isOpen = Boolean(visitId && visit);

  return isOrganizationAdminRoleSelected || isProfessionalRoleSelected ? (
    <OrganizationVisitModal isOpen={isOpen} onClose={onClose} visit={visit} />
  ) : (
    <VisitModal isOpen={isOpen} onClose={onClose} visit={visit} />
  );
}
